'use client';

import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';

// IMPORTANT: Replace ALL bracketed placeholders like [Your Company Name],
// [Date], [Contact Email], [Governing Law Jurisdiction], etc., with your actual information.
// This is template content and requires review by a legal professional.

export default function TermsOfServiceClient() {
    const { t } = useTranslation();
    const lastUpdatedDate = t('terms.lastUpdatedDate', {
        fallback: 'April 16, 2025',
    });

    return (
        <div className="container mx-auto max-w-3xl px-4 py-12 md:py-16">
            <h1 className="text-3xl font-bold mb-4 text-foreground">
                {t('terms.title', { fallback: 'Terms of Service' })}
            </h1>
            <p className="text-sm text-muted-foreground mb-8">
                {t('terms.lastUpdatedPrefix', { fallback: 'Last updated:' })}{' '}
                {lastUpdatedDate}
            </p>

            <div className="prose dark:prose-invert max-w-none space-y-6">
                {/* Introduction */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.introduction.title', {
                            fallback: '1. Introduction & Agreement',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.introduction.p1', {
                            fallback:
                                'Welcome to URLPrompter ("we", "us", "our"). These Terms of Service ("Terms") govern your use of our website and services (collectively, the "Service").',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.introduction.p2', {
                            fallback:
                                'By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access the Service. These Terms apply to all visitors, users, and others who access or use the Service.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.introduction.p3', {
                            fallback:
                                'Your access to and use of the Service is also conditioned on your acceptance of and compliance with our Privacy Policy. Please read our Privacy Policy carefully before using our Service.',
                        })}
                    </p>
                </section>

                {/* Use of Service */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.useOfService.title', {
                            fallback: '2. Use of Service',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.useOfService.eligibility.p1', {
                            fallback:
                                'You must be at least 18 years old to use the Service.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.useOfService.license.p1', {
                            fallback:
                                'We grant you a limited, non-exclusive, non-transferable, revocable license to use the Service strictly in accordance with these Terms.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.useOfService.restrictions.p1', {
                            fallback: 'You agree not to use the Service:',
                        })}
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                        <li>
                            {t('terms.useOfService.restrictions.item1', {
                                fallback:
                                    'In any way that violates any applicable national or international law or regulation.',
                            })}
                        </li>
                        <li>
                            {t('terms.useOfService.restrictions.item2', {
                                fallback:
                                    'For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way.',
                            })}
                        </li>
                        <li>
                            {t('terms.useOfService.restrictions.item3', {
                                fallback:
                                    'To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail", "chain letter," "spam," or any other similar solicitation.',
                            })}
                        </li>
                        <li>
                            {t('terms.useOfService.restrictions.item4', {
                                fallback:
                                    'To impersonate or attempt to impersonate URLPrompter, an employee, another user, or any other person or entity.',
                            })}
                        </li>
                        <li>
                            {t('terms.useOfService.restrictions.item5', {
                                fallback:
                                    "To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or which, as determined by us, may harm us or users of the Service or expose them to liability.",
                            })}
                        </li>
                        <li>
                            {t('terms.useOfService.restrictions.item6', {
                                fallback:
                                    'To attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the Service, the server on which the Service is stored, or any server, computer, or database connected to the Service.',
                            })}
                        </li>
                        <li>
                            {t('terms.useOfService.restrictions.item7', {
                                fallback:
                                    'To submit URLs to content that is illegal, harmful, infringing, defamatory, obscene, or otherwise objectionable.',
                            })}
                        </li>
                    </ul>
                </section>

                {/* Accounts */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.accounts.title', {
                            fallback: '3. User Accounts',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.accounts.p1', {
                            fallback:
                                'When you create an account with us, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.accounts.p2', {
                            fallback:
                                'You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.',
                        })}
                    </p>
                </section>

                {/* API Usage */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.apiUsage.title', {
                            fallback: '4. API Usage',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.apiUsage.p1', {
                            fallback:
                                'If you use our API, you are granted access based on your subscription plan. You are responsible for securing your API key.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.apiUsage.p2', {
                            fallback:
                                'Usage is subject to the limits defined in your plan. Exceeding these limits may result in throttling or require upgrading your plan.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.apiUsage.p3', {
                            fallback:
                                'You agree not to abuse the API, attempt to circumvent limitations, or use it for any purpose that violates these Terms.',
                        })}
                    </p>
                </section>

                {/* Subscriptions and Payments */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.payments.title', {
                            fallback: '5. Subscriptions and Payments',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.payments.p1', {
                            fallback:
                                'Some parts of the Service are billed on a subscription basis ("Subscription(s)"). You will be billed in advance on a recurring and periodic basis ("Billing Cycle"). Billing cycles are set typically on a monthly or annual basis, depending on the subscription plan you select.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.payments.p2', {
                            fallback:
                                'At the end of each Billing Cycle, your Subscription will automatically renew under the exact same conditions unless you cancel it or we cancel it. You may cancel your Subscription renewal either through your online account management page or by contacting customer support.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.payments.p3', {
                            fallback:
                                'A valid payment method, including credit card, is required to process the payment for your Subscription. You shall provide URLPrompter (or Stripe) with accurate and complete billing information. By submitting such payment information, you automatically authorize us (or Stripe) to charge all Subscription fees incurred through your account to any such payment instruments.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.payments.p4', {
                            fallback:
                                'Fee Changes: We, in our sole discretion and at any time, may modify the Subscription fees. Any Subscription fee change will become effective at the end of the then-current Billing Cycle. We will provide you with reasonable prior notice of any change in Subscription fees to give you an opportunity to terminate your Subscription before such change becomes effective. Your continued use of the Service after the Subscription fee change comes into effect constitutes your agreement to pay the modified Subscription fee amount.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.payments.p5', {
                            fallback:
                                'Refunds: Except when required by law, paid Subscription fees are non-refundable.',
                        })}
                    </p>
                </section>

                {/* Intellectual Property */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.intellectualProperty.title', {
                            fallback: '6. Intellectual Property',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.intellectualProperty.p1', {
                            fallback:
                                'The Service and its original content (excluding Content provided by users), features, and functionality are and will remain the exclusive property of URLPrompter and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.intellectualProperty.p2', {
                            fallback:
                                'Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of URLPrompter.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.intellectualProperty.p3', {
                            fallback:
                                'Regarding the output generated by the Service based on your prompts and provided URLs: You retain ownership of the prompts you provide. Subject to your compliance with these Terms, we grant you a license to use the output generated by the Service for your purposes.',
                        })}
                    </p>
                </section>

                {/* Disclaimers */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.disclaimers.title', {
                            fallback: '7. Disclaimers',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.disclaimers.p1', {
                            fallback:
                                'Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.disclaimers.p2', {
                            fallback:
                                'URLPrompter, its subsidiaries, affiliates, and its licensors do not warrant that a) the Service will function uninterrupted, secure or available at any particular time or location; b) any errors or defects will be corrected; c) the Service is free of viruses or other harmful components; or d) the results of using the Service (including the accuracy, reliability, or appropriateness of AI-generated content) will meet your requirements.',
                        })}
                    </p>
                </section>

                {/* Limitation of Liability */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.liability.title', {
                            fallback: '8. Limitation of Liability',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.liability.p1', {
                            fallback:
                                'In no event shall URLPrompter, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Service; (ii) any conduct or content of any third party on the Service; (iii) any content obtained from the Service (including AI-generated output); and (iv) unauthorized access, use or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence) or any other legal theory, whether or not we have been informed of the possibility of such damage, and even if a remedy set forth herein is found to have failed of its essential purpose.',
                        })}
                    </p>
                </section>

                {/* Indemnification */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.indemnification.title', {
                            fallback: '9. Indemnification',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.indemnification.p1', {
                            fallback:
                                "You agree to defend, indemnify and hold harmless URLPrompter and its licensee and licensors, and their employees, contractors, agents, officers and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees), resulting from or arising out of a) your use and access of the Service, by you or any person using your account and password; b) a breach of these Terms, or c) Content posted on the Service.",
                        })}
                    </p>
                </section>

                {/* Termination */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.termination.title', {
                            fallback: '10. Termination',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.termination.p1', {
                            fallback:
                                'We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.termination.p2', {
                            fallback:
                                'If you wish to terminate your account, you may simply discontinue using the Service or cancel your subscription.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.termination.p3', {
                            fallback:
                                'All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity and limitations of liability.',
                        })}
                    </p>
                </section>

                {/* Governing Law */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.governingLaw.title', {
                            fallback: '11. Governing Law',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.governingLaw.p1', {
                            fallback:
                                'These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.',
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.governingLaw.p2', {
                            fallback:
                                'Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect. These Terms constitute the entire agreement between us regarding our Service, and supersede and replace any prior agreements we might have had between us regarding the Service.',
                        })}
                    </p>
                </section>

                {/* Changes to Terms */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.changes.title', {
                            fallback: '12. Changes to Terms',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.changes.p1', {
                            fallback:
                                "We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.",
                        })}
                    </p>
                    <p className="mb-4">
                        {t('terms.changes.p2', {
                            fallback:
                                'By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service.',
                        })}
                    </p>
                </section>

                {/* Contact Us */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        {t('terms.contact.title', {
                            fallback: '13. Contact Us',
                        })}
                    </h2>
                    <p className="mb-4">
                        {t('terms.contact.p1', {
                            fallback:
                                'If you have any questions about these Terms, please contact us:',
                        })}
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-1">
                        <li>
                            {t('terms.contact.item1', {
                                fallback: 'By email: support@urlprompter.com',
                            })}
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    );
}
